import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

interface ValueHistogramProps {
  /** One entry per bar, already normalized so the tallest is 1. */
  buckets: number[];
  /** Height of the tallest bar, in pixels. */
  heightPx: number;
  /** Selected span as fractions of the axis, for dimming what's excluded. */
  selectionStart: number;
  selectionEnd: number;
}

/**
 * Distribution of the result set behind a range slider, so the range being
 * chosen reads against where the products actually are.
 *
 * Decorative — the slider beside it already announces the selected values —
 * so it is hidden from assistive tech rather than described twice.
 */
export const ValueHistogram: React.FC<ValueHistogramProps> = ({
  buckets,
  heightPx,
  selectionStart,
  selectionEnd,
}) => (
  <div
    aria-hidden
    className="flex w-full items-end gap-0.5"
    style={{ height: heightPx }}
  >
    {buckets.map((bucket, index) => {
      // Bars are a fixed positional axis, so the index is the bar's identity.
      const position = (index + 0.5) / buckets.length;
      return (
        <div
          key={index}
          className={barStyle.cls({
            isInRange: position >= selectionStart && position <= selectionEnd,
          })}
          style={{
            height: Math.max(bucket * heightPx, EMPTY_BUCKET_HEIGHT_PX),
          }}
        />
      );
    })}
  </div>
);

/** Empty buckets keep a sliver so the axis reads as continuous. */
const EMPTY_BUCKET_HEIGHT_PX = 5;

const barStyle = tsv({
  base: "min-w-px flex-1 rounded-t-[2px]",
  variants: {
    isInRange: {
      true: "bg-fs-text-primary",
      false: "bg-fs-border-muted",
    },
  },
});
